import { httpClient } from '../utils/httpClient';
import {
  UsuarioListItem, UsuarioPayload, PagedResponse,
  AñoEscolar, Grado, Seccion, Curso, RolItem,
  UsuarioMasivoItem, UsuarioMasivoResponse,
} from '../types/usuario.types';
import { catalogStorage } from '../utils/catalogStorage';

// ============================================================
// usuarioService.ts
// httpClient ya extrae body.data y lanza Error(body.message)
// si success === false. Solo construimos la URL y el payload.
// ============================================================

export const usuarioService = {

  async listar(params: {
    page?: number; size?: number;
    rol?: string; activo?: boolean; busqueda?: string;
  } = {}): Promise<PagedResponse<UsuarioListItem>> {
    const q = new URLSearchParams({
      page: String(params.page ?? 0),
      size: String(params.size ?? 10),
      ...(params.rol      !== undefined && params.rol      !== '' && { rol:      params.rol }),
      ...(params.activo   !== undefined                            && { activo:   String(params.activo) }),
      ...(params.busqueda !== undefined && params.busqueda !== '' && { busqueda: params.busqueda }),
    });
    return httpClient.get<PagedResponse<UsuarioListItem>>(`/usuarios?${q}`);
  },

  async obtener(id: number): Promise<UsuarioListItem> {
    return httpClient.get<UsuarioListItem>(`/usuarios/${id}`);
  },

  // Campos exactos que espera el backend
  async crear(payload: UsuarioPayload): Promise<UsuarioListItem> {
    return httpClient.post<UsuarioListItem>('/usuarios', payload);
  },

  async modificar(id: number, payload: Partial<UsuarioPayload>): Promise<UsuarioListItem> {
    return httpClient.put<UsuarioListItem>(`/usuarios/${id}`, payload);
  },

  async cambiarEstado(id: number, activo: boolean): Promise<void> {
    return httpClient.patch<void>(`/usuarios/${id}/estado`, { activo });
  },

  async eliminar(id: number): Promise<void> {
    return httpClient.delete<void>(`/usuarios/${id}`);
  },

  async registrarMasivo(items: UsuarioMasivoItem[]): Promise<UsuarioMasivoResponse> {
    return httpClient.post<UsuarioMasivoResponse>('/usuarios/masivo', items);
  },

  async listarEstudiantes(params: {
    activo?: boolean; search?: string;
  } = {}): Promise<UsuarioListItem[]> {
    const q = new URLSearchParams({
      ...(params.activo !== undefined && { activo: String(params.activo) }),
      ...(params.search !== undefined && params.search !== '' && { search: params.search }),
    });
    const qs = q.toString();
    const res = await httpClient.get<UsuarioListItem[] | { content: UsuarioListItem[] }>(
      `/usuarios/estudiantes${qs ? `?${qs}` : ''}`
    );
    return Array.isArray(res) ? res : (res.content ?? []);
  },
};

// ── Catálogos ─────────────────────────────────────────────────
// Lee del localStorage si ya fue cargado; si no, consulta el backend
// y guarda el resultado para futuras llamadas.

export const catalogoService = {

  async getAñosEscolares(): Promise<AñoEscolar[]> {
    const cached = catalogStorage.getAñosEscolares();
    if (cached) return cached;
    const data = (await httpClient.get<AñoEscolar[]>('/anios-escolares'))
      .filter(a => a.activo)
      .sort((a, b) => b.anio - a.anio);   // mayor año primero
    catalogStorage.saveAñosEscolares(data);
    return data;
  },

  async getGrados(): Promise<Grado[]> {
    const cached = catalogStorage.getGrados();
    if (cached) return cached;
    const data = (await httpClient.get<Grado[]>('/grados')).filter(g => g.activo);
    catalogStorage.saveGrados(data);
    return data;
  },

  async getSecciones(): Promise<Seccion[]> {
    const cached = catalogStorage.getSecciones();
    if (cached) return cached;
    const data = (await httpClient.get<Seccion[]>('/secciones')).filter(s => s.activo);
    catalogStorage.saveSecciones(data);
    return data;
  },

  async getCursos(): Promise<Curso[]> {
    const cached = catalogStorage.getCursos();
    if (cached) return cached;
    const data = (await httpClient.get<Curso[]>('/cursos')).filter(c => c.activo);
    catalogStorage.saveCursos(data);
    return data;
  },

  async getRoles(): Promise<RolItem[]> {
    const cached = catalogStorage.getRoles();
    if (cached) return cached;
    const data = await httpClient.get<RolItem[]>('/roles');
    catalogStorage.saveRoles(data);
    return data;
  },

  // Fuerza refetch del backend ignorando el caché (útil para admin)
  async refrescarCache(): Promise<void> {
    catalogStorage.clearAll();
  },

  async refrescar(): Promise<void> {
    catalogStorage.clearAll();
    await Promise.all([
      this.getAñosEscolares(),
      this.getGrados(),
      this.getSecciones(),
      this.getCursos(),
    ]);
  },
};

// ── Años Escolares (CRUD admin) ────────────────────────────────
export const anioEscolarService = {

  async listarTodos(): Promise<AñoEscolar[]> {
    const data = await httpClient.get<AñoEscolar[]>('/anios-escolares');
    return data.sort((a, b) => b.anio - a.anio);
  },

  async crear(payload: { anio: number; fase?: 'REGULAR' | 'RECUPERACION'; fechaInicio?: string | null; fechaFin?: string | null }): Promise<AñoEscolar> {
    const result = await httpClient.post<AñoEscolar>('/anios-escolares', payload);
    catalogStorage.clearAll();
    return result;
  },

  async actualizar(id: number, payload: Partial<Pick<AñoEscolar, 'anio' | 'activo' | 'fase' | 'fechaInicio' | 'fechaFin'>>): Promise<AñoEscolar> {
    const result = await httpClient.put<AñoEscolar>(`/anios-escolares/${id}`, payload);
    catalogStorage.clearAll();
    return result;
  },
};